import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  GraduationCap, Search, RefreshCw, Trash2, Eye, MessageSquare, Plus,
  Phone, Mail, User, Copy, X, Send, FileText, Link2, Download,
  CheckCircle2, XCircle, RotateCcw, AlertCircle, FolderOpen,
  ZoomIn, ChevronLeft, ChevronRight, RotateCw, ExternalLink, Calculator, Pencil,
  KeyRound, Eye as EyeIcon, EyeOff, UserCheck, Calendar as CalendarIcon,
  ChevronsRight
} from 'lucide-react';
import { euros, pourcent, montantSaisi, calculerPaie, dirhams, TAUX_MAD } from './FormuleAssu';

import { useRafraichissementAuto } from '../rafraichissementAuto';
import { copierTexte } from '../pressePapier';
// Suivi des candidats à la formation téléopérateur assurance.
// Même mécanique que les leads : arrivée par la page publique /formation-assurance,
// puis qualification par statut, notes, commentaires et assignation.

const STATUTS = [
  { key: 'nouveau', label: 'Nouveau', color: '#667eea' },
  { key: 'contacte', label: 'Contacté', color: '#3aa0d1' },
  { key: 'nrp', label: 'NRP', color: '#9e9e9e' },
  { key: 'interesse', label: 'Intéressé', color: '#f0a020' },
  { key: 'entretien_prevu', label: 'Entretien prévu', color: '#7e57c2' },
  { key: 'en_formation', label: 'En formation', color: '#00a3a3' },
  { key: 'valide', label: 'Validé', color: '#2e9e5b' },
  { key: 'pas_interesse', label: 'Pas intéressé', color: '#c07a7a' },
  { key: 'refuse', label: 'Refusé', color: '#c62828' }
];

const statutInfo = (key) => STATUTS.find(s => s.key === key) || { label: key, color: '#5f6b85' };

// État du dossier d'adhésion, distinct du statut de suivi du candidat.
const STATUTS_DOSSIER = [
  { key: 'envoye', label: 'Lien envoyé', color: '#7a839b' },
  { key: 'consulte', label: 'Lien ouvert', color: '#3aa0d1' },
  { key: 'en_cours', label: 'En cours de dépôt', color: '#f0a020' },
  { key: 'soumis', label: 'À valider', color: '#7e57c2' },
  { key: 'valide', label: 'Dossier validé', color: '#2e9e5b' },
  { key: 'refuse', label: 'Dossier refusé', color: '#c62828' }
];

const dossierInfo = (key) => STATUTS_DOSSIER.find(s => s.key === key) || null;

function CandidatsAssu({ currentUser }) {
  const [candidats, setCandidats] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copieConditions, setCopieConditions] = useState(false);
  const [copieCalcul, setCopieCalcul] = useState(false);
  const [caPaie, setCaPaie] = useState('');
  const [addForm, setAddForm] = useState({ nom: '', nom_crm: '', mdp_crm: '', telephone_whatsapp: '', email_pro: '', message: '' });
  // Fenêtre de modification de la fiche : null tant qu'elle est fermée.
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  // Le mot de passe CRM reste masqué tant qu'on ne l'a pas demandé.
  const [mdpVisible, setMdpVisible] = useState(false);
  const [mdpCopie, setMdpCopie] = useState(false);
  // Accès à l'espace personnel du candidat et contrats qu'il y saisit.
  const [espaceMdpVisible, setEspaceMdpVisible] = useState(false);
  const [espaceCopie, setEspaceCopie] = useState(false);
  const [espaceCopieTout, setEspaceCopieTout] = useState(false);
  const [espaceBusy, setEspaceBusy] = useState(false);
  const [espaceErreur, setEspaceErreur] = useState('');
  const [moisContrats, setMoisContrats] = useState(() => moisCourant());
  const [cles, setCles] = useState({ conditions: null, paie: null });
  // Barème ajusté à la main pour un candidat : null tant qu'on n'édite pas.
  const [baremeEdit, setBaremeEdit] = useState(null);
  const [baremeBusy, setBaremeBusy] = useState(false);
  const [contrats, setContrats] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [dossierBusy, setDossierBusy] = useState(false);
  const [dossierErreur, setDossierErreur] = useState('');
  // Autres fiches qui désignent la même personne.
  const [doublons, setDoublons] = useState([]);
  const [fusionBusy, setFusionBusy] = useState(0);
  const [lienCopie, setLienCopie] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');
  const [saisieRefus, setSaisieRefus] = useState(false);
  // Aperçus des pièces : chaque fichier est récupéré authentifié puis transformé
  // en URL locale, seule façon d'afficher une image derrière un en-tête Bearer.
  const [apercus, setApercus] = useState({});
  const [visionneuse, setVisionneuse] = useState(null);
  const [rotation, setRotation] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  // `isAdmin` commande aussi la colonne « Agent » et son menu d'assignation :
  // le RH ne l'a pas, la route d'assignation lui étant fermée.
  const isAdmin = currentUser && ['admin', 'sous_admin'].includes(currentUser.role);
  // Le valideur consulte, il ne modifie rien ici.
  const lectureSeule = currentUser && currentUser.role === 'valideur';
  // Le dossier d'adhésion (lien, envoi, validation) est ouvert à l'admin et au RH.
  // Le backend refuse ces routes à tout autre rôle, sous_admin compris.
  const peutGererDossier = currentUser && ['admin', 'rh'].includes(currentUser.role);
  // Supprimer un dossier reste à l'admin seul, comme supprimer une fiche.
  const peutSupprimerDossier = currentUser && currentUser.role === 'admin';
  // La modification d'une fiche passe par une route ouverte à l'admin et au RH :
  // ni le sous-admin ni le valideur ne voient le bouton.
  const peutModifierFiche = currentUser && ['admin', 'rh'].includes(currentUser.role);
  // Le serveur ne renvoie le mot de passe CRM qu'à l'admin : inutile de prévoir
  // la case pour les autres rôles.
  const peutVoirMdp = currentUser && currentUser.role === 'admin';

  const publicUrl = `${window.location.origin}/formation-assurance`;
  // Les clés des pages publiques ne sont plus écrites dans le code du navigateur :
  // le serveur ne les donne qu'à un compte du CRM, et celle du calcul de paie
  // n'est servie qu'à l'admin.
  // Adresses courtes : c'est ce qu'on colle dans un message.
  const conditionsUrl = cles.conditions
    ? `${window.location.origin}/c/${encodeURIComponent(cles.conditions)}`
    : '';
  const calculPaieUrl = cles.paie
    ? `${window.location.origin}/p/${encodeURIComponent(cles.paie)}`
    : '';

  const fetchCandidats = useCallback(async (silencieux = false) => {
    try {
      if (!silencieux) setLoading(true);
      const params = new URLSearchParams();
      if (filterStatut !== 'tous') params.append('statut', filterStatut);
      if (searchTerm) params.append('search', searchTerm);
      const res = await axios.get(`${API_URL}/api/candidats-assu?${params.toString()}`, authHeader);
      setCandidats(res.data);
    } catch (e) {
      console.error('Erreur chargement candidats:', e);
    } finally {
      setLoading(false);
    }
  }, [filterStatut, searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/stats`, authHeader);
      setStats(res.data);
    } catch (e) {
      console.error('Erreur stats candidats:', e);
    }
  }, []);

  // Mise a jour automatique de la page (voir src/rafraichissementAuto.js)
  useRafraichissementAuto(() => { fetchCandidats(true); fetchStats() }, { secondes: 30 });

  useEffect(() => {
    fetchCandidats();
    fetchStats();
  }, [fetchCandidats, fetchStats]);

  useEffect(() => {
    axios.get(`${API_URL}/api/candidats-assu/liens`, authHeader)
      .then(res => setCles(res.data))
      .catch(() => setCles({ conditions: null, paie: null }));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API_URL}/api/users`, authHeader)
      .then(res => setUsers(res.data.filter(u => ['agent', 'admin', 'commercial'].includes(u.role))))
      .catch(() => {});
  }, [isAdmin]);

  const changerStatut = async (id, statut) => {
    try {
      await axios.patch(`${API_URL}/api/candidats-assu/${id}/statut`, { statut }, authHeader);
      fetchCandidats();
      fetchStats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors du changement de statut');
    }
  };

  const assigner = async (id, agent_id) => {
    try {
      await axios.patch(`${API_URL}/api/candidats-assu/${id}/assigner`, { agent_id: agent_id || null }, authHeader);
      fetchCandidats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de l\'assignation');
    }
  };

  const supprimer = async (c) => {
    if (!window.confirm(`Supprimer définitivement la candidature de ${c.nom} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/candidats-assu/${c.id}`, authHeader);
      if (selected && selected.id === c.id) setSelected(null);
      fetchCandidats();
      fetchStats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const estImage = (doc) =>
    /^image\//.test(doc.mime || '') || /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(doc.nom_fichier || '');

  // Récupère le contenu d'une pièce et le garde en URL locale (une seule fois).
  const chargerApercu = async (doc) => {
    if (apercus[doc.id]) return apercus[doc.id];
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/documents/${doc.id}/fichier`, {
        ...authHeader, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      setApercus(prev => ({ ...prev, [doc.id]: url }));
      return url;
    } catch (e) {
      return null;
    }
  };

  const oublierApercus = () => {
    setApercus(prev => {
      Object.values(prev).forEach(u => { try { window.URL.revokeObjectURL(u); } catch (_) {} });
      return {};
    });
  };

  const chargerDossier = async (candidatId) => {
    setDossierErreur('');
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/${candidatId}/dossier`, authHeader);
      setDossier(res.data);
      // Les aperçus sont un confort : s'ils échouent, ils ne doivent pas emporter
      // le dossier avec eux et laisser la fiche sur « Chargement… ».
      try {
        (res.data.documents || []).filter(estImage).forEach(chargerApercu);
      } catch (apercuErreur) {
        console.error('Aperçus indisponibles :', apercuErreur);
      }
    } catch (e) {
      setDossier(null);
      setDossierErreur(e.response?.data?.error || 'Le dossier n\'a pas pu être chargé.');
    }
  };

  const chargerDoublons = async (candidatId) => {
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/${candidatId}/doublons`, authHeader);
      setDoublons(res.data.doublons || []);
    } catch (e) {
      setDoublons([]);
    }
  };

  // Réunit une fiche en double sur celle qui est ouverte. Rien n'est perdu :
  // pièces, historique et contrats suivent, le dossier le plus avancé est gardé.
  const fusionner = async (doublon) => {
    if (!window.confirm(
      `Réunir la fiche de ${doublon.nom} (créée le ${formatDate(doublon.created_at)}) sur celle-ci ?\n\n`
      + `Ses pièces, son historique et ses contrats seront rattachés ici, et sa fiche en double sera supprimée. `
      + `Le dossier le plus avancé des deux est conservé.`
    )) return;
    setFusionBusy(doublon.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/candidats-assu/${selected.id}/fusionner`, { doublon_id: doublon.id }, authHeader
      );
      setSelected(prev => ({ ...prev, ...res.data.candidat }));
      await chargerDossier(selected.id);
      chargerCommentaires(selected.id);
      chargerDoublons(selected.id);
      chargerContrats(selected.id, moisContrats);
      fetchCandidats();
      fetchStats();
    } catch (e) {
      alert(e.response?.data?.error || 'La fusion n\'a pas pu être faite.');
    } finally {
      setFusionBusy(0);
    }
  };

  const chargerCommentaires = async (candidatId) => {
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/${candidatId}/comments`, authHeader);
      setComments(res.data);
    } catch (e) {
      setComments([]);
    }
  };

  const ouvrirFiche = async (c) => {
    setSelected(c);
    setNotesDraft(c.notes || '');
    setNewComment('');
    setDossier(null);
    setMotifRefus('');
    setSaisieRefus(false);
    setLienCopie(false);
    setCaPaie('');
    setMdpVisible(false);
    setMdpCopie(false);
    setEspaceMdpVisible(false);
    setEspaceCopie(false);
    setEspaceCopieTout(false);
    setEspaceErreur('');
    setContrats(null);
    setDoublons([]);
    setBaremeEdit(null);
    setMoisContrats(moisCourant());
    oublierApercus();
    chargerDossier(c.id);
    chargerContrats(c.id, moisCourant());
    chargerCommentaires(c.id);
    chargerDoublons(c.id);
  };

  const ajouterCommentaire = async () => {
    const txt = newComment.trim();
    if (!txt) return;
    try {
      const res = await axios.post(`${API_URL}/api/candidats-assu/${selected.id}/comments`, { comment: txt }, authHeader);
      setComments([...comments, res.data]);
      setNewComment('');
      fetchCandidats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de l\'ajout du commentaire');
    }
  };

  const enregistrerNotes = async () => {
    try {
      await axios.patch(`${API_URL}/api/candidats-assu/${selected.id}/notes`, { notes: notesDraft }, authHeader);
      setSelected({ ...selected, notes: notesDraft });
      fetchCandidats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const ajouterCandidat = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/candidats-assu`, addForm, authHeader);
      setShowAddModal(false);
      setAddForm({ nom: '', nom_crm: '', mdp_crm: '', telephone_whatsapp: '', email_pro: '', message: '' });
      fetchCandidats();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  const copierMdpCrm = async () => {
    if (!await copierTexte(selected.mdp_crm)) {
      alert('La copie n\'a pas fonctionné. Affichez le mot de passe et copiez-le à la main.');
      return;
    }
    setMdpCopie(true);
    setTimeout(() => setMdpCopie(false), 2000);
  };

  // ---- Espace personnel du candidat ----

  const ouvrirEspace = async (renouveler = false) => {
    if (renouveler && !window.confirm(
      'Renouveler le mot de passe invalide celui déjà transmis au candidat. Continuer ?'
    )) return;
    setEspaceBusy(true);
    setEspaceErreur('');
    try {
      const res = await axios.post(
        `${API_URL}/api/candidats-assu/${selected.id}/espace`, { renouveler }, authHeader
      );
      setSelected(prev => ({ ...prev, ...res.data.candidat }));
      setEspaceMdpVisible(true);
      chargerCommentaires(selected.id);
      fetchCandidats();
    } catch (e) {
      setEspaceErreur(e.response?.data?.error || 'Impossible d\'ouvrir l\'accès.');
    } finally {
      setEspaceBusy(false);
    }
  };

  const fermerEspace = async () => {
    if (!window.confirm('Fermer l\'accès supprime le compte du candidat. Ses contrats sont conservés. Continuer ?')) return;
    setEspaceBusy(true);
    try {
      await axios.delete(`${API_URL}/api/candidats-assu/${selected.id}/espace`, authHeader);
      setSelected(prev => ({ ...prev, user_id: null, espace_mdp: null }));
      chargerCommentaires(selected.id);
      fetchCandidats();
    } catch (e) {
      setEspaceErreur(e.response?.data?.error || 'Impossible de fermer l\'accès.');
    } finally {
      setEspaceBusy(false);
    }
  };

  // Le bouton collé au mot de passe ne copie que le mot de passe : collé dans un
  // champ de connexion, un bloc « Identifiant : … / Mot de passe : … » ne marche pas.
  const copierEspaceMdp = async () => {
    if (!await copierTexte(selected.espace_mdp)) {
      alert('La copie n\'a pas fonctionné. Affichez le mot de passe et copiez-le à la main.');
      return;
    }
    setEspaceCopie(true);
    setTimeout(() => setEspaceCopie(false), 2000);
  };

  // Les deux lignes ensemble, pour un message écrit à la main.
  const copierEspaceIdentifiants = async () => {
    const ok = await copierTexte(
      `Identifiant : ${selected.email_pro}\nMot de passe : ${selected.espace_mdp || ''}`
    );
    if (!ok) {
      alert('La copie n\'a pas fonctionné.');
      return;
    }
    setEspaceCopieTout(true);
    setTimeout(() => setEspaceCopieTout(false), 2000);
  };

  const whatsAppEspace = () => {
    const num = String(selected.telephone_whatsapp || '').replace(/\D/g, '');
    const texte = `Bonjour ${selected.nom},\n\nVotre accès personnel est ouvert : ${window.location.origin}\n\n`
      + `Identifiant : ${selected.email_pro}\nMot de passe : ${selected.espace_mdp || ''}\n\n`
      + `Vous y retrouvez votre fiche et le calendrier où saisir vos contrats.`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(texte)}`, '_blank');
  };

  // ---- Barème personnalisé d'un candidat ----

  // Les taux se saisissent en pourcentage, c'est ainsi qu'on en parle.
  const ouvrirBareme = (regle) => setBaremeEdit({
    fixe: String(regle.fixe ?? 0),
    seuil: String(regle.seuil ?? 0),
    retenue: String(Math.round((regle.retenue || 0) * 100)),
    taux: String(Math.round((regle.taux || 0) * 100))
  });

  const enregistrerBareme = async () => {
    setBaremeBusy(true);
    try {
      await axios.patch(
        `${API_URL}/api/candidats-assu/${selected.id}/dossier/bareme`, baremeEdit, authHeader
      );
      setBaremeEdit(null);
      await chargerDossier(selected.id);
      chargerCommentaires(selected.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Enregistrement impossible.');
    } finally {
      setBaremeBusy(false);
    }
  };

  const reinitialiserBareme = async () => {
    if (!window.confirm('Revenir au barème de la formule pour ce candidat ?')) return;
    setBaremeBusy(true);
    try {
      await axios.patch(
        `${API_URL}/api/candidats-assu/${selected.id}/dossier/bareme`, { reinitialiser: true }, authHeader
      );
      setBaremeEdit(null);
      await chargerDossier(selected.id);
      chargerCommentaires(selected.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Retour impossible.');
    } finally {
      setBaremeBusy(false);
    }
  };

  // ---- Contrats du candidat ----

  const chargerContrats = useCallback(async (candidatId, mois) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/candidats-assu/${candidatId}/contrats?mois=${mois}`, authHeader
      );
      setContrats(res.data);
      // Le chiffre d'affaires du calcul de paie suit le mois affiché : c'est ce
      // que le candidat a saisi, l'admin n'a plus à le retaper.
      setCaPaie(res.data.total_ttc ? String(res.data.total_ttc) : '');
      return res.data;
    } catch (e) {
      setContrats(null);
      return null;
    }
  }, [API_URL]);

  // ---- Modification manuelle d'une fiche ----

  // Une date SQL peut arriver en ISO complet : le champ `date` veut aaaa-mm-jj.
  const dateChamp = (valeur) => valeur ? String(valeur).slice(0, 10) : '';

  const ouvrirEdition = () => setEditForm({
    nom: selected.nom || '',
    nom_crm: selected.nom_crm || '',
    mdp_crm: selected.mdp_crm || '',
    telephone_whatsapp: selected.telephone_whatsapp || '',
    email_pro: selected.email_pro || '',
    statut: selected.statut || 'nouveau',
    date_relance: dateChamp(selected.date_relance),
    message: selected.message || ''
  });

  const enregistrerFiche = async (e) => {
    e.preventDefault();
    setEditBusy(true);
    try {
      const res = await axios.put(`${API_URL}/api/candidats-assu/${selected.id}`, {
        ...editForm,
        date_relance: editForm.date_relance || null
      }, authHeader);
      // La route renvoie la ligne brute : on garde les champs calculés de la
      // fiche déjà chargée (agent, créateur, état du dossier).
      setSelected(prev => ({ ...prev, ...res.data }));
      setEditForm(null);
      fetchCandidats();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setEditBusy(false);
    }
  };

  // ---- Dossier d'adhésion ----

  const lienDossier = (d) => d && d.token ? `${window.location.origin}/dossier-candidat/${d.token}` : '';

  const messageWhatsApp = (c, d) =>
    `Bonjour ${c.nom},\n\nVoici votre dossier d'adhésion : choisissez la formule qui vous convient `
    + `et déposez vos documents directement depuis ce lien.\n\n${lienDossier(d)}\n\n`
    + `Ce lien vous est personnel, merci de ne pas le transmettre.`;

  const creerDossier = async (regenerer = false) => {
    if (regenerer && !window.confirm(
      'Régénérer le lien invalide définitivement celui déjà envoyé au candidat. Continuer ?'
    )) return;
    setDossierBusy(true);
    try {
      await axios.post(`${API_URL}/api/candidats-assu/${selected.id}/dossier`, { regenerer }, authHeader);
      await chargerDossier(selected.id);
      fetchCandidats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la création du dossier');
    } finally {
      setDossierBusy(false);
    }
  };

  const copierLienDossier = () => {
    navigator.clipboard.writeText(lienDossier(dossier.dossier)).then(() => {
      setLienCopie(true);
      setTimeout(() => setLienCopie(false), 2000);
    }).catch(() => {});
  };

  const ouvrirWhatsApp = () => {
    const num = String(selected.telephone_whatsapp || '').replace(/\D/g, '');
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(messageWhatsApp(selected, dossier.dossier))}`, '_blank');
  };

  // Les pièces ne sont pas servies en statique : on les récupère authentifié, puis
  // on déclenche le téléchargement depuis le blob.
  const telechargerDocument = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/documents/${doc.id}/fichier`, {
        ...authHeader, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nom_fichier;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert('Impossible de télécharger ce document');
    }
  };

  const ouvrirDocument = async (doc) => {
    const url = await chargerApercu(doc);
    if (url) window.open(url, '_blank');
    else alert('Impossible d\'ouvrir ce document');
  };

  const imagesDuDossier = () => ((dossier && dossier.documents) || []).filter(estImage);

  const ouvrirVisionneuse = (doc) => {
    const index = imagesDuDossier().findIndex(d => d.id === doc.id);
    setRotation(0);
    setVisionneuse(index >= 0 ? index : 0);
  };

  const deplacerVisionneuse = (pas) => {
    const images = imagesDuDossier();
    if (images.length === 0) return;
    setRotation(0);
    setVisionneuse((visionneuse + pas + images.length) % images.length);
  };

  const deciderDossier = async (decision) => {
    if (decision === 'refuse' && motifRefus.trim().length < 3) {
      alert('Indiquez le motif du refus, il est affiché au candidat.');
      return;
    }
    setDossierBusy(true);
    try {
      await axios.patch(`${API_URL}/api/candidats-assu/${selected.id}/dossier/decision`,
        { decision, motif_refus: motifRefus }, authHeader);
      await chargerDossier(selected.id);
      const res = await axios.get(`${API_URL}/api/candidats-assu/${selected.id}/comments`, authHeader);
      setComments(res.data);
      setSaisieRefus(false);
      setMotifRefus('');
      fetchCandidats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la décision');
    } finally {
      setDossierBusy(false);
    }
  };

  const revoquerDossier = async () => {
    if (!window.confirm('Supprimer le dossier et toutes les pièces envoyées par le candidat ? Cette action est définitive.')) return;
    setDossierBusy(true);
    try {
      await axios.delete(`${API_URL}/api/candidats-assu/${selected.id}/dossier`, authHeader);
      await chargerDossier(selected.id);
      fetchCandidats();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la suppression du dossier');
    } finally {
      setDossierBusy(false);
    }
  };

  const copierLien = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const copierCalculPaie = async () => {
    if (!await copierTexte(calculPaieUrl)) { alert('La copie n\'a pas fonctionné.'); return; }
    setCopieCalcul(true);
    setTimeout(() => setCopieCalcul(false), 2000);
  };

  const copierConditions = async () => {
    if (!await copierTexte(conditionsUrl)) { alert('La copie n\'a pas fonctionné.'); return; }
    setCopieConditions(true);
    setTimeout(() => setCopieConditions(false), 2000);
  };

  const waLink = (tel) => `https://wa.me/${String(tel || '').replace(/\D/g, '')}`;

  const formatDate = (d) => d ? new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : '—';

  // Une date seule arrive en « aaaa-mm-jj… » : la relire avec `new Date` la
  // ramènerait à minuit UTC, donc à la veille dans les fuseaux derrière UTC.
  const formatJour = (d) => {
    if (!d) return '—';
    const [annee, mois, jour] = String(d).slice(0, 10).split('-');
    return jour ? `${jour}/${mois}/${annee}` : String(d);
  };

  return (
    <div style={{ padding: 20 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: '#fff', fontSize: 24, display: 'flex', alignItems: 'center', gap: 9 }}>
            <GraduationCap size={24} style={{ color: '#fff' }} /> Candidat Assu
          </h2>
          <p style={{ margin: '5px 0 0', color: '#fff', fontSize: 14 }}>
            Candidatures issues de la page de formation téléopérateur assurance
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={copierLien} style={{
            padding: '10px 18px', backgroundColor: copied ? '#2e9e5b' : '#6c757d', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 6
          }}>
            <Copy size={15} /> {copied ? 'Lien copié !' : 'Copier lien Formation'}
          </button>
          {conditionsUrl && (
          <button onClick={copierConditions} title="Lien des conditions, clé comprise" style={{
            padding: '10px 18px', backgroundColor: copieConditions ? '#2e9e5b' : '#00a3a3', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 6
          }}>
            <FolderOpen size={15} /> {copieConditions ? 'Lien copié !' : 'Copier lien Conditions'}
          </button>
          )}
          {calculPaieUrl && (
          <button onClick={copierCalculPaie} title="Lien du calcul de paie, clé comprise" style={{
            padding: '10px 18px', backgroundColor: copieCalcul ? '#2e9e5b' : '#7e57c2', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 6
          }}>
            <Calculator size={15} /> {copieCalcul ? 'Lien copié !' : 'Copier lien Calcul paie'}
          </button>
          )}
          {!lectureSeule && (
          <button onClick={() => setShowAddModal(true)} style={{
            padding: '10px 18px', backgroundColor: '#2d3f7f', color: '#fff', border: 'none',
            borderRadius: 8, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6
          }}>
            <Plus size={15} /> Ajouter un candidat
          </button>
          )}
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{
            padding: '12px 18px', borderRadius: 10, background: '#2d3f7f', color: '#fff', minWidth: 110
          }}>
            <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 600 }}>TOTAL</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{stats.total}</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>dont {stats.aujourd_hui} aujourd'hui</div>
          </div>
          {STATUTS.map(s => (
            <div key={s.key}
              onClick={() => setFilterStatut(filterStatut === s.key ? 'tous' : s.key)}
              style={{
                padding: '12px 16px', borderRadius: 10, background: '#fff', cursor: 'pointer',
                border: `2px solid ${filterStatut === s.key ? s.color : s.color + '25'}`, minWidth: 92
              }}>
              <div style={{ fontSize: 11, color: '#5f6b85', fontWeight: 600 }}>{s.label.toUpperCase()}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{stats[s.key] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#7a839b' }} />
          <input
            type="text"
            placeholder="Nom, nom CRM, email, téléphone…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 36px', border: '1.5px solid #ddd',
              borderRadius: 8, fontSize: 14, boxSizing: 'border-box'
            }}
          />
        </div>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          style={{ padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14 }}
        >
          <option value="tous">Tous les statuts</option>
          {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button onClick={() => { fetchCandidats(); fetchStats(); }} style={{
          padding: '10px 16px', background: '#fff', border: '1.5px solid #ddd', borderRadius: 8,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14
        }}>
          <RefreshCw size={15} /> Actualiser
        </button>
      </div>

      {/* Tableau */}
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #eee' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f7f8fb' }}>
                <th style={th}>Candidat</th>
                <th style={th}>WhatsApp</th>
                <th style={th}>Email pro</th>
                <th style={th}>Statut</th>
                <th style={th}>Dossier</th>
                {isAdmin && <th style={th}>Agent</th>}
                <th style={th}>Reçu le</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? 8 : 7} style={{ padding: 40, textAlign: 'center', color: '#5f6b85' }}>Chargement…</td></tr>
              ) : candidats.length === 0 ? (
                <tr><td colSpan={isAdmin ? 8 : 7} style={{ padding: 40, textAlign: 'center', color: '#5f6b85' }}>
                  Aucun candidat pour le moment.
                </td></tr>
              ) : candidats.map(c => (
                <tr key={c.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <User size={14} style={{ color: '#7a839b' }} /> {c.nom}
                    </div>
                    {c.nom_crm && (
                      <div style={{ fontSize: 12, color: '#5f6b85', marginTop: 3 }}>CRM : {c.nom_crm}</div>
                    )}
                    {c.message && (
                      <div style={{ fontSize: 12, color: '#5f6b85', marginTop: 3, maxWidth: 240 }}>{c.message}</div>
                    )}
                  </td>
                  <td style={td}>
                    <a href={waLink(c.telephone_whatsapp)} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#2e9e5b', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Phone size={13} /> {c.telephone_whatsapp}
                    </a>
                  </td>
                  <td style={td}>
                    <a href={`mailto:${c.email_pro}`} style={{ color: '#2d3f7f', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Mail size={13} /> {c.email_pro}
                    </a>
                  </td>
                  <td style={td}>
                    <select
                      value={c.statut}
                      disabled={lectureSeule}
                      onChange={(e) => changerStatut(c.id, e.target.value)}
                      style={{
                        padding: '5px 8px', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                        color: statutInfo(c.statut).color, border: `1.5px solid ${statutInfo(c.statut).color}55`,
                        background: statutInfo(c.statut).color + '12'
                      }}
                    >
                      {STATUTS.map(s => <option key={s.key} value={s.key} style={{ color: '#333' }}>{s.label}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    {dossierInfo(c.dossier_statut) ? (
                      <span title={c.dossier_formule || ''} style={{
                        fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                        whiteSpace: 'nowrap',
                        color: dossierInfo(c.dossier_statut).color,
                        background: dossierInfo(c.dossier_statut).color + '18'
                      }}>{dossierInfo(c.dossier_statut).label}</span>
                    ) : (
                      <span style={{ color: '#c9cfe0' }}>—</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td style={td}>
                      <select
                        value={c.agent_id || ''}
                        onChange={(e) => assigner(c.id, e.target.value)}
                        style={{ padding: '5px 8px', borderRadius: 6, fontSize: 12.5, border: '1.5px solid #ddd' }}
                      >
                        <option value="">Non assigné</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.nom}</option>)}
                      </select>
                    </td>
                  )}
                  <td style={{ ...td, color: '#5f6b85', fontSize: 13 }}>{formatDate(c.created_at)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => ouvrirFiche(c)} title="Voir la fiche" style={iconBtn}>
                      <Eye size={16} />
                      {c.nb_comments > 0 && (
                        <span style={{
                          fontSize: 10, background: '#2d3f7f', color: '#fff', borderRadius: 8,
                          padding: '1px 5px', marginLeft: 3
                        }}>{c.nb_comments}</span>
                      )}
                    </button>
                    {isAdmin && !lectureSeule && (
                      <button onClick={() => supprimer(c)} title="Supprimer" style={{ ...iconBtn, color: '#c62828' }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fiche candidat */}
      {selected && (
        <div style={overlay} onClick={() => setSelected(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, color: '#2d3f7f', fontSize: 20 }}>{selected.nom}</h3>
                <div style={{ fontSize: 13, color: '#5f6b85', marginTop: 4 }}>
                  Candidature reçue le {formatDate(selected.created_at)} · source : {selected.source}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {peutModifierFiche && (
                  <button
                    onClick={ouvrirEdition}
                    title="Modifier la fiche"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff',
                      border: '1.5px solid #2d3f7f', color: '#2d3f7f', borderRadius: 8,
                      padding: '7px 13px', cursor: 'pointer', fontSize: 13.5, fontWeight: 600
                    }}
                  >
                    <Pencil size={14} /> Modifier
                  </button>
                )}
                <button onClick={() => setSelected(null)} style={{ ...iconBtn, color: '#5f6b85' }}><X size={20} /></button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
              <div style={infoBox}>
                <div style={infoLabel}>WhatsApp</div>
                <a href={waLink(selected.telephone_whatsapp)} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#2e9e5b', fontWeight: 600, textDecoration: 'none' }}>
                  {selected.telephone_whatsapp}
                </a>
              </div>
              <div style={infoBox}>
                <div style={infoLabel}>Email professionnel</div>
                <a href={`mailto:${selected.email_pro}`} style={{ color: '#2d3f7f', fontWeight: 600, textDecoration: 'none' }}>
                  {selected.email_pro}
                </a>
              </div>
              <div style={infoBox}>
                <div style={infoLabel}>Statut</div>
                <span style={{ color: statutInfo(selected.statut).color, fontWeight: 700 }}>
                  {statutInfo(selected.statut).label}
                </span>
              </div>
              <div style={infoBox}>
                <div style={infoLabel}>Agent assigné</div>
                <span style={{ fontWeight: 600 }}>{selected.agent_nom || 'Non assigné'}</span>
              </div>
              <div style={infoBox}>
                <div style={infoLabel}>Formule choisie</div>
                {(selected.dossier_formule || (dossier && dossier.dossier && dossier.dossier.formule_nom)) ? (
                  <span style={{ fontWeight: 700, color: '#2d3f7f' }}>
                    {selected.dossier_formule || dossier.dossier.formule_nom}
                  </span>
                ) : (
                  <span style={{ fontWeight: 600, color: '#a0a7ba' }}>Pas encore choisie</span>
                )}
              </div>
              {selected.date_relance && (
                <div style={infoBox}>
                  <div style={infoLabel}>Date de relance</div>
                  <span style={{ fontWeight: 600 }}>{formatJour(selected.date_relance)}</span>
                </div>
              )}
              <div style={{ ...infoBox, gridColumn: peutVoirMdp ? undefined : '1 / -1' }}>
                <div style={infoLabel}>Nom dans le CRM</div>
                <span style={{ fontWeight: 600, color: selected.nom_crm ? undefined : '#a0a7ba' }}>
                  {selected.nom_crm || 'Non renseigné par le candidat'}
                </span>
              </div>
              {peutVoirMdp && (
                <div style={infoBox}>
                  <div style={{ ...infoLabel, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <KeyRound size={12} /> MOT DE PASSE CRM
                  </div>
                  {selected.mdp_crm ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        fontWeight: 600, fontFamily: mdpVisible ? 'monospace' : 'inherit',
                        letterSpacing: mdpVisible ? 0 : 2, wordBreak: 'break-all'
                      }}>
                        {mdpVisible ? selected.mdp_crm : '••••••••'}
                      </span>
                      <button
                        onClick={() => setMdpVisible(v => !v)}
                        title={mdpVisible ? 'Masquer' : 'Afficher'}
                        style={{ ...iconBtn, padding: 2 }}
                      >
                        {mdpVisible ? <EyeOff size={15} /> : <EyeIcon size={15} />}
                      </button>
                      <button onClick={copierMdpCrm} title="Copier" style={{ ...iconBtn, padding: 2 }}>
                        {mdpCopie ? <CheckCircle2 size={15} style={{ color: '#2e9e5b' }} /> : <Copy size={15} />}
                      </button>
                    </div>
                  ) : (
                    <span style={{ fontWeight: 600, color: '#a0a7ba' }}>Non renseigné</span>
                  )}
                </div>
              )}
            </div>

            {selected.message && (
              <div style={{ ...infoBox, marginBottom: 18 }}>
                <div style={infoLabel}>Message du candidat</div>
                <div>{selected.message}</div>
              </div>
            )}

            {/* Fiches en double : même email ou même numéro */}
            {doublons.length > 0 && (
              <div style={{
                border: '1px solid #e6c98a', borderRadius: 12, padding: 16, marginBottom: 20, background: '#fdfaf3'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <AlertCircle size={17} style={{ color: '#b8860b' }} />
                  <span style={{ fontWeight: 700, color: '#8a6a12', fontSize: 15 }}>
                    {doublons.length === 1 ? 'Une autre fiche' : `${doublons.length} autres fiches`} pour cette personne
                  </span>
                </div>
                <div style={{ fontSize: 13.5, color: '#5f6b85', marginBottom: 11 }}>
                  Même email ou même numéro. Réunir une fiche ici rattache ses pièces, son historique
                  et ses contrats à celle-ci, garde le dossier le plus avancé des deux, et supprime le doublon.
                </div>
                {doublons.map(d => (
                  <div key={d.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    borderTop: '1px solid #f0e6cd', padding: '9px 0'
                  }}>
                    <div style={{ flex: '1 1 240px', fontSize: 13.5 }}>
                      <div style={{ fontWeight: 700 }}>{d.nom}</div>
                      <div style={{ color: '#5f6b85' }}>
                        inscrite le {formatDate(d.created_at)}
                        {d.dossier_statut && <> · dossier {d.dossier_statut}{d.dossier_formule ? ` · ${d.dossier_formule}` : ''}</>}
                        {' '}· {d.nb_documents} pièce{d.nb_documents > 1 ? 's' : ''}
                        {d.nb_contrats > 0 && <> · {d.nb_contrats} contrat{d.nb_contrats > 1 ? 's' : ''}</>}
                      </div>
                      {(d.telephone_whatsapp !== selected.telephone_whatsapp || d.email_pro !== selected.email_pro) && (
                        <div style={{ color: '#8a94ad', fontSize: 12.5 }}>
                          {d.telephone_whatsapp} · {d.email_pro}
                        </div>
                      )}
                    </div>
                    {peutGererDossier && (
                      <button onClick={() => fusionner(d)} disabled={fusionBusy === d.id} style={{
                        padding: '8px 15px', background: '#b8860b', color: '#fff', border: 'none',
                        borderRadius: 8, cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 6
                      }}>
                        <ChevronsRight size={14} />
                        {fusionBusy === d.id ? 'Fusion…' : 'Réunir sur cette fiche'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Dossier d'adhésion : formule choisie et pièces envoyées par le candidat */}
            <div style={{
              border: '1px solid #e7eaf3', borderRadius: 12, padding: 16, marginBottom: 20, background: '#fbfcff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <FolderOpen size={18} style={{ color: '#2d3f7f' }} />
                <span style={{ fontWeight: 800, color: '#2d3f7f', fontSize: 15 }}>Dossier d'adhésion</span>
                {dossier && dossier.dossier && dossierInfo(dossier.dossier.statut) && (
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                    color: dossierInfo(dossier.dossier.statut).color,
                    background: dossierInfo(dossier.dossier.statut).color + '18'
                  }}>{dossierInfo(dossier.dossier.statut).label}</span>
                )}
              </div>

              {!dossier ? (
                dossierErreur ? (
                  <div style={{ fontSize: 13.5 }}>
                    <div style={{ color: '#c62828', marginBottom: 8 }}>{dossierErreur}</div>
                    <button onClick={() => chargerDossier(selected.id)} style={lienBtn}>
                      <RefreshCw size={13} /> Réessayer
                    </button>
                  </div>
                ) : (
                  <div style={{ color: '#5f6b85', fontSize: 13.5 }}>Chargement…</div>
                )
              ) : !dossier.dossier ? (
                <>
                  {dossier.formules_incompletes && dossier.formules_incompletes.length > 0 && (
                    <div style={{
                      background: '#fdfaf3', border: '1px solid #e6c98a', borderRadius: 9,
                      padding: '10px 13px', marginBottom: 12, fontSize: 13.5, display: 'flex', gap: 8
                    }}>
                      <AlertCircle size={17} style={{ color: '#b8860b', flexShrink: 0, marginTop: 1 }} />
                      <span>À compléter dans <code style={{ margin: '0 4px' }}>backend/formules_assu.js</code> :
                      {' '}{dossier.formules_incompletes.join(', ')}. Le candidat ne voit que les formules renseignées.</span>
                    </div>
                  )}
                  <div style={{ color: '#5f6b85', fontSize: 13.5, marginBottom: 12 }}>
                    Aucun dossier envoyé. Créez le lien puis transmettez-le sur WhatsApp :
                    le candidat choisit sa formule et dépose ses pièces.
                  </div>
                  {peutGererDossier && (
                    <button onClick={() => creerDossier(false)} disabled={dossierBusy} style={{
                      padding: '10px 18px', background: '#2d3f7f', color: '#fff', border: 'none',
                      borderRadius: 8, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7
                    }}>
                      <Link2 size={15} /> {dossierBusy ? 'Création…' : 'Créer le lien du dossier'}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Le lien à envoyer */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                    <input
                      readOnly
                      value={lienDossier(dossier.dossier)}
                      onFocus={(e) => e.target.select()}
                      style={{
                        flex: '1 1 240px', padding: '9px 12px', border: '1.5px solid #ddd',
                        borderRadius: 8, fontSize: 13, background: '#fff', color: '#2f3648'
                      }}
                    />
                    <button onClick={copierLienDossier} style={{
                      padding: '9px 14px', background: lienCopie ? '#2e9e5b' : '#6c757d', color: '#fff',
                      border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13.5,
                      display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <Copy size={14} /> {lienCopie ? 'Copié !' : 'Copier'}
                    </button>
                    <button onClick={ouvrirWhatsApp} style={{
                      padding: '9px 14px', background: '#2e9e5b', color: '#fff', border: 'none',
                      borderRadius: 8, cursor: 'pointer', fontSize: 13.5,
                      display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <Phone size={14} /> Envoyer sur WhatsApp
                    </button>
                  </div>

                  {/* Formule choisie */}
                  <div style={{ ...infoBox, marginBottom: 12 }}>
                    <div style={infoLabel}>FORMULE CHOISIE PAR LE CANDIDAT</div>
                    {dossier.dossier.formule_id ? (
                      <div style={{ fontWeight: 700, color: '#2d3f7f' }}>
                        {dossier.dossier.formule_nom || dossier.dossier.formule_id}
                        <span style={{ fontWeight: 400, color: '#5f6b85', fontSize: 13 }}>
                          {' '}· choisie le {formatDate(dossier.dossier.choisi_at)}
                        </span>
                      </div>
                    ) : (
                      <div style={{ color: '#5f6b85' }}>Pas encore choisie</div>
                    )}
                    {dossier.dossier.signature_nom && (
                      <div style={{ fontSize: 13, color: '#5f6b85', marginTop: 4 }}>
                        Conditions acceptées et signées « {dossier.dossier.signature_nom} » le {formatDate(dossier.dossier.soumis_at)}
                      </div>
                    )}
                  </div>

                  {/* Calcul de la paie, sur la formule que le candidat a acceptée */}
                  {(() => {
                    const snapshot = dossier.dossier.formule_snapshot;
                    let acceptee = snapshot;
                    if (typeof snapshot === 'string') {
                      try { acceptee = JSON.parse(snapshot || 'null'); } catch (e) { acceptee = null; }
                    }
                    const courante = (dossier.formules || []).find(f => f.id === dossier.dossier.formule_id);
                    // La règle appliquée est celle que le candidat a acceptée ; la
                    // formule courante ne sert que si le dossier est antérieur.
                    const regle = (acceptee && acceptee.remuneration) || (courante && courante.remuneration) || null;
                    if (!regle) return null;
                    const calcul = calculerPaie(regle, caPaie);
                    return (
                      <div style={{
                        border: '1px solid #e0d7f2', borderRadius: 11, padding: 13,
                        marginBottom: 12, background: '#faf8ff'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9, flexWrap: 'wrap' }}>
                          <Calculator size={16} style={{ color: '#7e57c2' }} />
                          <span style={{ fontWeight: 700, color: '#5b3fa0', fontSize: 14 }}>Calcul de la paie</span>
                          {peutGererDossier && (
                            <button
                              onClick={() => baremeEdit ? setBaremeEdit(null) : ouvrirBareme(regle)}
                              style={{
                                marginLeft: 'auto', background: 'none', border: '1.5px solid #cbb8ee',
                                color: '#5b3fa0', borderRadius: 7, padding: '4px 10px', cursor: 'pointer',
                                fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5
                              }}
                            >
                              <Pencil size={12} /> {baremeEdit ? 'Fermer' : 'Ajuster ce barème'}
                            </button>
                          )}
                          <span style={{ fontSize: 12, color: '#7a839b', flexBasis: '100%' }}>
                            {regle.fixe > 0
                              ? `fixe ${euros(regle.fixe)} · ${pourcent(regle.taux)} au-delà de ${euros(regle.seuil)}`
                              : `${pourcent(regle.taux)} dès le premier euro`}
                            {' '}· base −{pourcent(regle.retenue)} de TVA
                          </span>
                        </div>
                        {baremeEdit && (
                          <div style={{
                            background: '#fff', border: '1px solid #e0d7f2', borderRadius: 9,
                            padding: 12, marginBottom: 11
                          }}>
                            <div style={{ fontSize: 12.5, color: '#5f6b85', marginBottom: 9 }}>
                              Ce barème ne vaut que pour ce candidat. Il est écrit dans son dossier :
                              modifier la formule générale ne le touchera plus.
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                              {[
                                { k: 'fixe', label: 'Fixe (€)' },
                                { k: 'seuil', label: 'Seuil (€)' },
                                { k: 'retenue', label: 'TVA (%)' },
                                { k: 'taux', label: 'Commission (%)' }
                              ].map(champ => (
                                <div key={champ.k}>
                                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: '#5b3fa0', marginBottom: 4 }}>
                                    {champ.label}
                                  </label>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={baremeEdit[champ.k]}
                                    onChange={(e) => setBaremeEdit({ ...baremeEdit, [champ.k]: e.target.value })}
                                    style={{
                                      width: '100%', padding: '9px 11px', border: '1.5px solid #ddd',
                                      borderRadius: 7, fontSize: 14, fontWeight: 700, boxSizing: 'border-box'
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 11, flexWrap: 'wrap' }}>
                              <button onClick={enregistrerBareme} disabled={baremeBusy} style={{
                                padding: '8px 16px', background: '#7e57c2', color: '#fff', border: 'none',
                                borderRadius: 7, cursor: 'pointer', fontSize: 13.5, fontWeight: 600
                              }}>{baremeBusy ? 'Enregistrement…' : 'Enregistrer ce barème'}</button>
                              <button onClick={reinitialiserBareme} disabled={baremeBusy} style={{
                                padding: '8px 16px', background: '#fff', color: '#5f6b85',
                                border: '1.5px solid #ddd', borderRadius: 7, cursor: 'pointer', fontSize: 13.5
                              }}>Revenir au barème de la formule</button>
                            </div>
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="CA TTC du mois"
                            value={caPaie}
                            onChange={(e) => setCaPaie(e.target.value)}
                            style={{
                              flex: '1 1 160px', padding: '10px 12px', border: '1.5px solid #ddd',
                              borderRadius: 8, fontSize: 15, fontWeight: 700, boxSizing: 'border-box'
                            }}
                          />
                          <div style={{
                            flex: '1 1 160px', background: '#7e57c2', color: '#fff', borderRadius: 8,
                            padding: '9px 14px', textAlign: 'right'
                          }}>
                            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 700 }}>À VERSER</div>
                            <div style={{ fontSize: 21, fontWeight: 800 }}>
                              {calcul ? dirhams(calcul.total) : '—'}
                            </div>
                            <div style={{ fontSize: 12.5, opacity: 0.85 }}>
                              {calcul ? `soit ${euros(calcul.total)}` : `conversion ×${TAUX_MAD}`}
                            </div>
                          </div>
                        </div>
                        {calcul && (
                          <div style={{ fontSize: 12.5, color: '#5f6b85', marginTop: 8, lineHeight: 1.6 }}>
                            {calcul.seuil > 0 && (
                              <>Part au-dessus de {euros(calcul.seuil)} : {euros(calcul.tranche)} · </>
                            )}
                            base après −{pourcent(calcul.retenue)} : {euros(calcul.base)} ·
                            commission {pourcent(calcul.taux)} : <strong>{euros(calcul.commission)}</strong>
                            {calcul.fixe > 0 && <> · fixe : {euros(calcul.fixe)}</>}
                            {calcul.seuil > 0 && calcul.tranche === 0 && (
                              <div style={{ color: '#b8860b', marginTop: 3 }}>
                                Seuil non atteint : seul le fixe est dû.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Pièces */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ ...infoLabel, marginBottom: 7 }}>
                      PIÈCES ({(dossier.documents || []).length} reçue{(dossier.documents || []).length > 1 ? 's' : ''})
                    </div>
                    {(dossier.documents_requis || []).map(t => {
                      const fichiers = (dossier.documents || []).filter(d => d.type_doc === t.id);
                      return (
                        <div key={t.id} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 0',
                          borderTop: '1px solid #eef0f6'
                        }}>
                          {fichiers.length > 0
                            ? <CheckCircle2 size={16} style={{ color: '#2e9e5b', flexShrink: 0, marginTop: 3 }} />
                            : <XCircle size={16} style={{ color: '#c9cfe0', flexShrink: 0, marginTop: 3 }} />}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t.label}</div>
                            {fichiers.length === 0 ? (
                              <div style={{ fontSize: 12.5, color: '#a0a7ba' }}>Non fournie</div>
                            ) : (
                              <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 6 }}>
                                {fichiers.map(f => (estImage(f) ? (
                                  <div key={f.id} style={{ width: 92 }}>
                                    <div
                                      onClick={() => ouvrirVisionneuse(f)}
                                      title={`${f.nom_fichier} — cliquer pour agrandir`}
                                      style={{
                                        width: 92, height: 92, borderRadius: 10, overflow: 'hidden',
                                        border: '1px solid #e7eaf3', background: '#f7f8fb', cursor: 'zoom-in',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        position: 'relative'
                                      }}
                                    >
                                      {apercus[f.id] ? (
                                        <img
                                          src={apercus[f.id]}
                                          alt={f.nom_fichier}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                      ) : (
                                        <span style={{ fontSize: 11, color: '#a0a7ba' }}>Chargement…</span>
                                      )}
                                      <span style={{
                                        position: 'absolute', right: 4, bottom: 4, background: 'rgba(0,0,0,0.55)',
                                        color: '#fff', borderRadius: 6, padding: 3, display: 'flex'
                                      }}><ZoomIn size={12} /></span>
                                    </div>
                                    <div style={{
                                      fontSize: 11, color: '#7a839b', marginTop: 3, overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                    }} title={f.nom_fichier}>{f.nom_fichier}</div>
                                  </div>
                                ) : (
                                  <div key={f.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e7eaf3',
                                    borderRadius: 10, padding: '8px 11px', background: '#fff'
                                  }}>
                                    <FileText size={15} style={{ color: '#c0392b' }} />
                                    <span style={{ fontSize: 12.5, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {f.nom_fichier}
                                    </span>
                                    <button onClick={() => ouvrirDocument(f)} title="Ouvrir dans un onglet" style={{ ...iconBtn, padding: 3 }}>
                                      <ExternalLink size={14} />
                                    </button>
                                    <button onClick={() => telechargerDocument(f)} title="Télécharger" style={{ ...iconBtn, padding: 3, color: '#5f6b85' }}>
                                      <Download size={14} />
                                    </button>
                                  </div>
                                )))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Décision */}
                  {dossier.dossier.statut === 'soumis' && peutGererDossier && (
                    <div style={{
                      background: '#f4f0fb', border: '1px solid #d8cdf0', borderRadius: 10,
                      padding: 13, marginBottom: 10
                    }}>
                      <div style={{ fontSize: 13.5, marginBottom: 10 }}>
                        Le candidat a transmis son dossier. Vérifiez les pièces, puis tranchez.
                      </div>
                      {saisieRefus ? (
                        <>
                          <textarea
                            rows={2}
                            value={motifRefus}
                            onChange={(e) => setMotifRefus(e.target.value)}
                            placeholder="Motif du refus — il sera affiché au candidat sur sa page"
                            style={{
                              width: '100%', padding: 10, border: '1.5px solid #ddd', borderRadius: 8,
                              fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 9
                            }}
                          />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => deciderDossier('refuse')} disabled={dossierBusy} style={{
                              padding: '9px 16px', background: '#c62828', color: '#fff', border: 'none',
                              borderRadius: 8, cursor: 'pointer', fontSize: 13.5
                            }}>Confirmer le refus</button>
                            <button onClick={() => setSaisieRefus(false)} style={{
                              padding: '9px 16px', background: '#fff', border: '1.5px solid #ddd',
                              borderRadius: 8, cursor: 'pointer', fontSize: 13.5
                            }}>Annuler</button>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button onClick={() => deciderDossier('valide')} disabled={dossierBusy} style={{
                            padding: '10px 18px', background: '#2e9e5b', color: '#fff', border: 'none',
                            borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 7
                          }}>
                            <CheckCircle2 size={15} /> Valider le dossier
                          </button>
                          <button onClick={() => setSaisieRefus(true)} style={{
                            padding: '10px 18px', background: '#fff', color: '#c62828',
                            border: '1.5px solid #e0a3a3', borderRadius: 8, cursor: 'pointer', fontSize: 14,
                            display: 'flex', alignItems: 'center', gap: 7
                          }}>
                            <XCircle size={15} /> Refuser
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {dossier.dossier.decision && (
                    <div style={{
                      fontSize: 13.5, marginBottom: 10,
                      color: dossier.dossier.decision === 'valide' ? '#2e9e5b' : '#c62828'
                    }}>
                      {dossier.dossier.decision === 'valide' ? 'Validé' : 'Refusé'} par{' '}
                      {dossier.dossier.decision_par_nom || 'un administrateur'} le {formatDate(dossier.dossier.decision_at)}
                      {dossier.dossier.motif_refus && <> — {dossier.dossier.motif_refus}</>}
                    </div>
                  )}

                  {peutGererDossier && (
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', paddingTop: 6, borderTop: '1px solid #eef0f6' }}>
                      <button onClick={() => creerDossier(true)} disabled={dossierBusy} style={lienBtn}>
                        <RotateCcw size={13} /> Régénérer le lien
                      </button>
                      {peutSupprimerDossier && (
                        <button onClick={revoquerDossier} disabled={dossierBusy} style={{ ...lienBtn, color: '#c62828' }}>
                          <Trash2 size={13} /> Supprimer le dossier
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Accès à l'espace personnel du candidat */}
            <div style={{
              border: '1px solid #e7eaf3', borderRadius: 12, padding: 16, marginBottom: 20, background: '#fbfcff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <UserCheck size={17} style={{ color: '#2d3f7f' }} />
                <span style={{ fontWeight: 700, color: '#2d3f7f', fontSize: 15 }}>Espace personnel du candidat</span>
              </div>

              {selected.user_id ? (
                <>
                  <div style={{ fontSize: 13.5, color: '#5f6b85', marginBottom: 10 }}>
                    Son accès est ouvert. Il s'y connecte avec son email et y saisit ses contrats.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div style={infoBox}>
                      <div style={infoLabel}>IDENTIFIANT</div>
                      <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{selected.email_pro}</span>
                    </div>
                    <div style={infoBox}>
                      <div style={infoLabel}>MOT DE PASSE</div>
                      {selected.espace_mdp ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            fontWeight: 600, fontFamily: espaceMdpVisible ? 'monospace' : 'inherit',
                            letterSpacing: espaceMdpVisible ? 0 : 2, wordBreak: 'break-all'
                          }}>
                            {espaceMdpVisible ? selected.espace_mdp : '••••••••'}
                          </span>
                          <button onClick={() => setEspaceMdpVisible(v => !v)} style={{ ...iconBtn, padding: 2 }}
                            title={espaceMdpVisible ? 'Masquer' : 'Afficher'}>
                            {espaceMdpVisible ? <EyeOff size={15} /> : <EyeIcon size={15} />}
                          </button>
                          <button onClick={copierEspaceMdp} title="Copier le mot de passe seul" style={{ ...iconBtn, padding: 2 }}>
                            {espaceCopie ? <CheckCircle2 size={15} style={{ color: '#2e9e5b' }} /> : <Copy size={15} />}
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontWeight: 600, color: '#a0a7ba' }}>Renouvelez-le pour le relire</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    <button onClick={whatsAppEspace} disabled={espaceBusy} style={lienBtn}>
                      <Send size={13} /> Envoyer sur WhatsApp
                    </button>
                    <button onClick={copierEspaceIdentifiants} style={lienBtn}>
                      {espaceCopieTout
                        ? <><CheckCircle2 size={13} style={{ color: '#2e9e5b' }} /> Copié</>
                        : <><Copy size={13} /> Copier identifiant + mot de passe</>}
                    </button>
                    <button onClick={() => ouvrirEspace(true)} disabled={espaceBusy} style={lienBtn}>
                      <RotateCcw size={13} /> Renouveler le mot de passe
                    </button>
                    {peutGererDossier && currentUser.role === 'admin' && (
                      <button onClick={fermerEspace} disabled={espaceBusy} style={{ ...lienBtn, color: '#c62828' }}>
                        <XCircle size={13} /> Fermer l'accès
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13.5, color: '#5f6b85', marginBottom: 10 }}>
                    Aucun accès ouvert. Il s'ouvre tout seul à la validation du dossier, ou ici à la main.
                  </div>
                  {peutGererDossier && (
                    <button onClick={() => ouvrirEspace(false)} disabled={espaceBusy} style={{
                      padding: '10px 18px', background: '#2d3f7f', color: '#fff', border: 'none',
                      borderRadius: 8, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7
                    }}>
                      <KeyRound size={15} /> {espaceBusy ? 'Ouverture…' : 'Ouvrir son accès'}
                    </button>
                  )}
                </>
              )}
              {espaceErreur && (
                <div style={{
                  background: '#fdf0f0', border: '1px solid #e0a3a3', color: '#c62828',
                  borderRadius: 9, padding: '10px 13px', marginTop: 11, fontSize: 13.5
                }}>{espaceErreur}</div>
              )}
            </div>

            {/* Contrats saisis par le candidat */}
            <div style={{
              border: '1px solid #e7eaf3', borderRadius: 12, padding: 16, marginBottom: 20, background: '#fbfcff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CalendarIcon size={17} style={{ color: '#2d3f7f' }} />
                  <span style={{ fontWeight: 700, color: '#2d3f7f', fontSize: 15 }}>Ses contrats</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="month"
                    value={moisContrats}
                    onChange={(e) => { setMoisContrats(e.target.value); chargerContrats(selected.id, e.target.value); }}
                    style={{ padding: '7px 10px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 13.5 }}
                  />
                </div>
              </div>

              {!contrats || contrats.nombre === 0 ? (
                <div style={{ fontSize: 13.5, color: '#8a94ad' }}>
                  Aucun contrat saisi sur ce mois.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <div style={{ ...infoBox, flex: '1 1 150px' }}>
                      <div style={infoLabel}>TOTAL TTC DU MOIS</div>
                      <span style={{ fontWeight: 800, fontSize: 19, color: '#2d3f7f' }}>{euros(contrats.total_ttc)}</span>
                    </div>
                    <div style={{ ...infoBox, flex: '1 1 110px' }}>
                      <div style={infoLabel}>CONTRATS</div>
                      <span style={{ fontWeight: 800, fontSize: 19, color: '#2d3f7f' }}>{contrats.nombre}</span>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                      <thead>
                        <tr>
                          <th style={{ ...th, padding: '8px 10px' }}>Date</th>
                          <th style={{ ...th, padding: '8px 10px' }}>Client</th>
                          <th style={{ ...th, padding: '8px 10px' }}>Téléphone</th>
                          <th style={{ ...th, padding: '8px 10px' }}>N° fiche</th>
                          <th style={{ ...th, padding: '8px 10px', textAlign: 'right' }}>Montant TTC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contrats.contrats.map(c => (
                          <tr key={c.id} style={{ borderTop: '1px solid #eef0f6' }}>
                            <td style={{ padding: '8px 10px' }}>{formatJour(c.date_contrat)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 600 }}>{c.client_nom}</td>
                            <td style={{ padding: '8px 10px' }}>
                              {c.client_telephone
                                ? <a href={waLink(c.client_telephone)} target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#2e9e5b', textDecoration: 'none' }}>{c.client_telephone}</a>
                                : <span style={{ color: '#c9cfe0' }}>—</span>}
                            </td>
                            <td style={{ padding: '8px 10px' }}>{c.numero_fiche || <span style={{ color: '#c9cfe0' }}>—</span>}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#2d3f7f' }}>
                              {euros(c.montant_ttc)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ fontSize: 12.5, color: '#8a94ad', marginTop: 9, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <ChevronsRight size={13} /> Ce total remplit le chiffre d'affaires du calcul de paie ci-dessus.
                  </div>
                </>
              )}
            </div>

            {/* Note libre */}
            <div style={{ marginBottom: 20 }}>
              <div style={infoLabel}>Note (modifiable)</div>
              <textarea
                value={notesDraft}
                readOnly={lectureSeule}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={3}
                style={{
                  width: '100%', padding: 11, border: '1.5px solid #ddd', borderRadius: 8,
                  fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical'
                }}
              />
              {!lectureSeule && (
              <button onClick={enregistrerNotes} style={{
                marginTop: 8, padding: '8px 16px', background: '#6c757d', color: '#fff',
                border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13
              }}>Enregistrer la note</button>
              )}
            </div>

            {/* Historique des commentaires */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12, color: '#2d3f7f', fontWeight: 700 }}>
                <MessageSquare size={17} /> Historique des échanges ({comments.length})
              </div>
              {comments.length === 0 && (
                <div style={{ color: '#5f6b85', fontSize: 13.5, marginBottom: 12 }}>Aucun commentaire pour le moment.</div>
              )}
              {comments.map(cm => (
                <div key={cm.id} style={{
                  background: '#f7f8fb', borderLeft: '3px solid #2d3f7f', borderRadius: '0 8px 8px 0',
                  padding: '10px 14px', marginBottom: 9
                }}>
                  <div style={{ fontSize: 12, color: '#5f6b85', marginBottom: 3 }}>
                    <strong style={{ color: '#2d3f7f' }}>{cm.auteur_nom || 'Utilisateur'}</strong> · {formatDate(cm.created_at)}
                  </div>
                  <div style={{ fontSize: 14 }}>{cm.comment}</div>
                </div>
              ))}
              {!lectureSeule && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') ajouterCommentaire(); }}
                  placeholder="Ajouter un commentaire…"
                  style={{ flex: 1, padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14 }}
                />
                <button onClick={ajouterCommentaire} style={{
                  padding: '10px 16px', background: '#2d3f7f', color: '#fff', border: 'none',
                  borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
                }}>
                  <Send size={15} /> Ajouter
                </button>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Visionneuse d'images */}
      {visionneuse !== null && imagesDuDossier()[visionneuse] && (() => {
        const images = imagesDuDossier();
        const doc = images[visionneuse];
        return (
          <div
            onClick={() => setVisionneuse(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,18,28,0.92)', zIndex: 2000,
              display: 'flex', flexDirection: 'column', padding: 16
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#fff', marginBottom: 12, flexWrap: 'wrap' }}
            >
              <span style={{ fontWeight: 700, fontSize: 14.5 }}>{doc.nom_fichier}</span>
              <span style={{ fontSize: 12.5, opacity: 0.7 }}>
                {visionneuse + 1} / {images.length}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button onClick={() => setRotation((rotation + 90) % 360)} title="Pivoter" style={boutonVisionneuse}>
                  <RotateCw size={17} />
                </button>
                <button onClick={() => ouvrirDocument(doc)} title="Ouvrir dans un onglet" style={boutonVisionneuse}>
                  <ExternalLink size={17} />
                </button>
                <button onClick={() => telechargerDocument(doc)} title="Télécharger" style={boutonVisionneuse}>
                  <Download size={17} />
                </button>
                <button onClick={() => setVisionneuse(null)} title="Fermer" style={boutonVisionneuse}>
                  <X size={17} />
                </button>
              </div>
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minHeight: 0 }}>
              {images.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); deplacerVisionneuse(-1); }} style={boutonVisionneuse}>
                  <ChevronLeft size={22} />
                </button>
              )}
              <div style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {apercus[doc.id] ? (
                  <img
                    src={apercus[doc.id]}
                    alt={doc.nom_fichier}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                      transform: `rotate(${rotation}deg)`, transition: 'transform .2s', borderRadius: 8
                    }}
                  />
                ) : (
                  <span style={{ color: '#fff' }}>Chargement…</span>
                )}
              </div>
              {images.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); deplacerVisionneuse(1); }} style={boutonVisionneuse}>
                  <ChevronRight size={22} />
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Ajout manuel */}
      {showAddModal && (
        <div style={overlay} onClick={() => setShowAddModal(false)}>
          <div style={{ ...modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, color: '#2d3f7f', fontSize: 19 }}>Ajouter un candidat</h3>
              <button onClick={() => setShowAddModal(false)} style={{ ...iconBtn, color: '#5f6b85' }}><X size={20} /></button>
            </div>
            <form onSubmit={ajouterCandidat} autoComplete="off">
              {[
                { k: 'nom', label: 'Nom et prénom', type: 'text', required: true, ph: 'Jean Dupont' },
                { k: 'telephone_whatsapp', label: 'Numéro WhatsApp', type: 'tel', required: true, ph: '+33 6 12 34 56 78' },
                { k: 'email_pro', label: 'Email professionnel', type: 'email', required: true, ph: 'prenom.nom@exemple.fr' },
                { k: 'nom_crm', label: 'Nom dans le CRM', type: 'text', required: false, ph: 'Jean D.' },
                { k: 'mdp_crm', label: 'Mot de passe CRM', type: 'text', required: false, ph: 'Le mot de passe du compte' }
              ].map(f => (
                <div key={f.k} style={{ marginBottom: 13 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2d3f7f', marginBottom: 5 }}>
                    {f.label} {f.required
                      ? <span style={{ color: '#c62828' }}>*</span>
                      : <span style={{ color: '#8a94ad', fontWeight: 500 }}>(facultatif)</span>}
                  </label>
                  <input
                    type={f.type}
                    required={f.required}
                    placeholder={f.ph}
                    value={addForm[f.k]}
                    onChange={(e) => setAddForm({ ...addForm, [f.k]: e.target.value })}
                    style={{
                      width: '100%', padding: '11px 13px', border: '1.5px solid #ddd',
                      borderRadius: 8, fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2d3f7f', marginBottom: 5 }}>
                  Message / contexte
                </label>
                <textarea
                  rows={3}
                  value={addForm.message}
                  onChange={(e) => setAddForm({ ...addForm, message: e.target.value })}
                  style={{
                    width: '100%', padding: 11, border: '1.5px solid #ddd', borderRadius: 8,
                    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical'
                  }}
                />
              </div>
              <button type="submit" style={{
                padding: '11px 22px', background: '#2d3f7f', color: '#fff', border: 'none',
                borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600
              }}>Enregistrer</button>
            </form>
          </div>
        </div>
      )}

      {/* Modification manuelle d'une fiche */}
      {editForm && selected && (
        <div style={{ ...overlay, zIndex: 1100 }} onClick={() => setEditForm(null)}>
          <div style={{ ...modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0, color: '#2d3f7f', fontSize: 19 }}>Modifier la fiche</h3>
              <button onClick={() => setEditForm(null)} style={{ ...iconBtn, color: '#5f6b85' }}><X size={20} /></button>
            </div>
            <div style={{ fontSize: 13, color: '#5f6b85', marginBottom: 16 }}>
              Candidature reçue le {formatDate(selected.created_at)} · source : {selected.source}
            </div>

            <form onSubmit={enregistrerFiche} autoComplete="off">
              {[
                { k: 'nom', label: 'Nom et prénom', type: 'text', required: true, ph: 'Jean Dupont' },
                { k: 'nom_crm', label: 'Nom dans le CRM', type: 'text', required: false, ph: 'Jean D.' },
                { k: 'mdp_crm', label: 'Mot de passe CRM', type: 'text', required: false, ph: 'Le mot de passe du compte' },
                { k: 'telephone_whatsapp', label: 'Numéro WhatsApp', type: 'tel', required: true, ph: '+33 6 12 34 56 78' },
                { k: 'email_pro', label: 'Email professionnel', type: 'email', required: true, ph: 'prenom.nom@exemple.fr' }
              ].map(f => (
                <div key={f.k} style={{ marginBottom: 13 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2d3f7f', marginBottom: 5 }}>
                    {f.label} {f.required
                      ? <span style={{ color: '#c62828' }}>*</span>
                      : <span style={{ color: '#8a94ad', fontWeight: 500 }}>(facultatif)</span>}
                  </label>
                  <input
                    type={f.type}
                    required={f.required}
                    placeholder={f.ph}
                    value={editForm[f.k]}
                    onChange={(e) => setEditForm({ ...editForm, [f.k]: e.target.value })}
                    style={champModale}
                  />
                </div>
              ))}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 13 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2d3f7f', marginBottom: 5 }}>
                    Statut
                  </label>
                  <select
                    value={editForm.statut}
                    onChange={(e) => setEditForm({ ...editForm, statut: e.target.value })}
                    style={champModale}
                  >
                    {STATUTS.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2d3f7f', marginBottom: 5 }}>
                    Date de relance <span style={{ color: '#8a94ad', fontWeight: 500 }}>(facultatif)</span>
                  </label>
                  <input
                    type="date"
                    value={editForm.date_relance}
                    onChange={(e) => setEditForm({ ...editForm, date_relance: e.target.value })}
                    style={champModale}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2d3f7f', marginBottom: 5 }}>
                  Message / contexte
                </label>
                <textarea
                  rows={3}
                  value={editForm.message}
                  onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                  style={{ ...champModale, fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>

              <div style={{ fontSize: 12.5, color: '#8a94ad', marginBottom: 14 }}>
                Le commentaire interne et l'assignation ne se modifient pas ici : ils gardent
                leurs propres zones sur la fiche.
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={editBusy} style={{
                  padding: '11px 22px', background: editBusy ? '#9aa4c4' : '#2d3f7f', color: '#fff',
                  border: 'none', borderRadius: 8, cursor: editBusy ? 'default' : 'pointer',
                  fontSize: 15, fontWeight: 600
                }}>{editBusy ? 'Enregistrement…' : 'Enregistrer'}</button>
                <button type="button" onClick={() => setEditForm(null)} style={{
                  padding: '11px 22px', background: '#fff', color: '#5f6b85', border: '1.5px solid #ddd',
                  borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600
                }}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Mois courant en heure locale : `toISOString()` est en UTC et basculerait un
// jour trop tôt le 1er du mois.
const moisCourant = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const th = { padding: '13px 14px', textAlign: 'left', fontSize: 12, color: '#5f6b85', fontWeight: 700, letterSpacing: 0.3 };
const td = { padding: '13px 14px', verticalAlign: 'middle', color: '#2f3648' };
const iconBtn = {
  background: 'none', border: 'none', cursor: 'pointer', color: '#2d3f7f',
  padding: 6, display: 'inline-flex', alignItems: 'center'
};
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 20, overflowY: 'auto'
};
const modal = {
  background: '#fff', borderRadius: 14, padding: 26, maxWidth: 680, width: '100%',
  margin: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
};
const boutonVisionneuse = {
  background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 9,
  padding: 9, cursor: 'pointer', display: 'inline-flex', alignItems: 'center'
};
const lienBtn = {
  background: 'none', border: 'none', cursor: 'pointer', color: '#5f6b85',
  padding: 0, fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 5
};
const champModale = {
  width: '100%', padding: '11px 13px', border: '1.5px solid #ddd',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff'
};
const infoBox = { background: '#f7f8fb', borderRadius: 9, padding: '11px 14px' };
const infoLabel = { fontSize: 11, color: '#5f6b85', fontWeight: 700, marginBottom: 4, letterSpacing: 0.3 };

export default CandidatsAssu;
